import { Category, useAlert } from '@drawbotics/react-drylus';
import { RouterService } from '@tokamakjs/common';
import { Controller, onDidMount, state, useAppContext } from '@tokamakjs/react';
import { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import { z } from 'zod';

import { SignupError } from '~/api';
import { AuthService } from '~/services';
import { AppContext, Invitation, InvitationStatus } from '~/types';
import { createTranslate } from '~/utils';

import { SignupView, createSignupSchema } from './signup.view';

const tt = createTranslate('routes.sign_up');
const ttErrors = createTranslate('errors');

@Controller({ view: SignupView })
export class SignupController {
  private _appContext = useAppContext<AppContext>();
  private _showAlert = useAlert().showAlert;

  @state isSubmitting?: boolean;

  @state invitation?: Invitation;
  @state invitationIsLoading = true;

  @state isHandlingSuccessfulGoogleSignup = false;
  @state isHandlingSuccessfulMicrosoftSignup = false;

  constructor(private readonly _router: RouterService, private readonly _authService: AuthService) {
    const user = this._authService.getUserFromLocalToken();
    if (user != null) {
      this._appContext.onFinish();
    }
  }

  @onDidMount()
  async onDidMount() {
    const user = this._authService.getUserFromLocalToken();
    if (user != null) {
      return this._appContext.onFinish();
    }

    const code = this._router.query.get('code');
    if (code) {
      const invitationRes = await this._authService.fetchInvitation(code);

      if (invitationRes.error) {
        this._showAlert({
          text: tt('generic_invitation_error'),
          category: Category.DANGER,
        });

        this.invitationIsLoading = false;

        return;
      }

      this.invitation = invitationRes.data;

      if (this.invitation.status === InvitationStatus.ACCEPTED) {
        this._showAlert({
          text: tt('code_already_used'),
          category: Category.INFO,
        });

        this._router.push('../login');

        return;
      } else {
        this.invitationIsLoading = false;
      }
    } else {
      this.invitationIsLoading = false;
    }
  }

  async signup(values: z.infer<ReturnType<typeof createSignupSchema>>) {
    this.isSubmitting = true;

    const res = await this._authService.signUp({
      email: values.email,
      password: values.password,
      first_name: values.firstName,
      last_name: values.lastName,
      locale: window.i18n.locale,
      invitation_token: this.invitation?.token,
    });

    if (res.error) {
      this.isSubmitting = false;

      let message;

      if (res.error === SignupError.TAKEN) {
        message = this.invitation != null ? tt('account_already_exists') : tt('email_in_use');
      } else {
        message = tt('generic_sign_up_error');
      }

      this._showAlert({
        text: message,
        category: Category.DANGER,
      });
    } else {
      this.afterSignup();
    }
  }

  async handleSuccessfulGoogleSignup(
    googleResponse: GoogleLoginResponse | GoogleLoginResponseOffline,
  ) {
    this.isHandlingSuccessfulGoogleSignup = true;

    const res = await this._authService.handleSuccessfulGoogleSignup(
      googleResponse,
      this.invitation?.token,
    );

    if (res.error) {
      if (res.error === 'user_already_exists') {
        this._showAlert({
          category: Category.DANGER,
          text: ttErrors('user_already_exists'),
        });
      } else {
        this._showAlert({
          text: tt('generic_sign_up_error'),
          category: Category.DANGER,
        });
      }

      this.isHandlingSuccessfulGoogleSignup = false;
    } else {
      this.afterSignup();
    }
  }

  async handleSuccessfulMicrosoftSignup(code: string) {
    this.isHandlingSuccessfulMicrosoftSignup = true;

    const res = await this._authService.handleSuccessfulMicrosoftSignup(
      code,
      this.invitation?.token,
    );

    if (res.error) {
      if (res.error === 'user_already_exists') {
        this._showAlert({
          category: Category.DANGER,
          text: ttErrors('user_already_exists'),
        });
      } else {
        this._showAlert({
          text: tt('generic_sign_up_error'),
          category: Category.DANGER,
        });
      }

      this.isHandlingSuccessfulMicrosoftSignup = false;
    } else {
      this.afterSignup();
    }
  }

  afterSignup() {
    if (this.invitation != null) {
      this._appContext.onFinish();
    } else {
      const search = this._router.rawQuery;
      const confirmationUrl = search ? `../confirmation${search}` : '../confirmation';
      this._router.push(confirmationUrl);
    }
  }
}
